import { inject, injectable } from "inversify";
import { DOMInjector } from "./react-app/DOMInjector";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { webScriptBackend } from "./webScriptBackend";
import { BlockWapChat } from "./react-app/BlockWapChat";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import { reaction } from "mobx";
import { backgroundClient } from "@whatslly/commons/background/client";

const BLOCK_WAP_CHAT_NAMESPACE = "namespace:block-wap-chat";
const BLUR_CHAT_CLASS = "tuvis-blur-chat";
const UN_BLUR_SELECTED_CHAT_CLASS = "tuvis-un-blur-selected-chat";

@injectable()
export class BlurMessageService {
  selectors?: IMSelectorsUnion;
  constructor(@inject(IMUserStore) private readonly imUserStore: IMUserStore) {}

  get whatsappMainChatContainer() {
    return document.querySelector<HTMLDivElement>(this.selectors?.css.main || "#main");
  }

  async init(selectors: IMSelectorsUnion) {
    this.selectors = selectors;
    webScriptBackend.on(WebScriptCommands.PERSON_CREATED, () => this.removeElement()).on(WebScriptCommands.LOGOUT, () => this.reset());

    reaction(
      () => this.isBlurContactEnabled,
      (value) => {
        if (value) {
          this.injectBlurChatClass();
        }
      }
    );
  }

  private getAppElement() {
    if (!this.selectors) {
      return;
    }
    return document.querySelector(this.selectors.css.app);
  }

  private injectAppClass(className: string) {
    const appElement = this.getAppElement();
    if (!appElement) {
      return;
    }
    appElement.classList.add(className);
  }

  private removeAppClass(className: string) {
    const appElement = this.getAppElement();
    if (!appElement) {
      return;
    }
    appElement.classList.remove(className);
  }

  injectBlurChatClass() {
    if (!this.isBlurContactEnabled) {
      return;
    }
    this.injectAppClass(BLUR_CHAT_CLASS);
  }

  removeBlurChatClass() {
    this.removeAppClass(BLUR_CHAT_CLASS);
  }

  injectUnBlurChatSelectedClass() {
    if (!this.isBlurContactEnabled) {
      return;
    }
    this.injectAppClass(UN_BLUR_SELECTED_CHAT_CLASS);
  }

  removeUnBlurChatSelectedClass() {
    this.removeAppClass(UN_BLUR_SELECTED_CHAT_CLASS);
  }

  injectElement({
    loading,
    isMultipleResults,
    unsupportedGroup,
    unknownGroupParticipants,
  }: {
    loading: boolean;
    isMultipleResults?: boolean;
    unsupportedGroup?: boolean;
    unknownGroupParticipants?: boolean;
  }) {
    const whatsappMainChatContainer = this.whatsappMainChatContainer;
    const isGroupBlocking = unknownGroupParticipants || unsupportedGroup;
    if (isGroupBlocking) {
      if (unsupportedGroup && !this.isEnabled) return;
      if (unknownGroupParticipants && !unsupportedGroup && !this.isGroupBlockingEnabled) return;
    } else {
      if (!this.isEnabled) return;
    }
    if (!whatsappMainChatContainer) return;
    DOMInjector.injectToDOM({
      host: whatsappMainChatContainer,
      Component: BlockWapChat,
      namespace: BLOCK_WAP_CHAT_NAMESPACE,
      props: { loading, isMultipleResults, unsupportedGroup, unknownGroupParticipants },
    });
    backgroundClient.send(BgCommands.SILENCE_NOTIFICATIONS, { silence: true });
  }

  removeElement() {
    DOMInjector.detachByNamespace(BLOCK_WAP_CHAT_NAMESPACE);
    backgroundClient.send(BgCommands.SILENCE_NOTIFICATIONS, { silence: false });
  }

  get isEnabled() {
    return Boolean(this.imUserStore.user?.blockUnknownPerson);
  }

  get isBlurContactEnabled() {
    return Boolean(this.imUserStore.user?.blurContactOnBlockUnknownPerson);
  }

  get isGroupBlockingEnabled() {
    return Boolean(this.imUserStore.user?.blockGroupsWithUnknownContacts);
  }

  reset() {
    this.removeElement();
    this.removeBlurChatClass();
    this.removeUnBlurChatSelectedClass();
  }
}
